import { chakra } from "@chakra-ui/react";

import { EMOTION_PANEL_BG, FOCUS_RING, PIX_BUTTON_BG } from "../../theme";

export const Shell = chakra("div", {
  base: {
    boxSizing: "border-box",
    width: "min(460px, 100%)",
    marginInline: "auto",
    padding: "16px",
    lg: { padding: "40px 24px" },
  },
  variants: {
    size: {
      wide: { lg: { width: "min(820px, 100%)" } },
      narrow: { lg: { width: "min(460px, 100%)" } },
      form: { lg: { width: "min(560px, 100%)" } },
    },
  },
  defaultVariants: { size: "wide" },
});

export const Card = chakra("section", {
  base: {
    background: "vbaby.card",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 10px 30px rgba(33, 33, 33, 0.08)",
  },
});

// Padding é variante porque prop de estilo não vence condição de breakpoint da
// receita: passar `padding` no uso seria sobrescrito no desktop.
export const EmotionPanel = chakra("div", {
  base: {
    display: "flex",
    flexDirection: "column",
    background: EMOTION_PANEL_BG,
    color: "white",
  },
  variants: {
    align: {
      center: { alignItems: "center", justifyContent: "center", textAlign: "center" },
      start: { alignItems: "flex-start", justifyContent: "center", textAlign: "left" },
    },
    density: {
      hero: { gap: "12px", padding: "28px 24px", lg: { padding: "40px 32px" } },
      header: { gap: "4px", padding: "18px 20px" },
    },
  },
  defaultVariants: { align: "center", density: "hero" },
});

export const IconCircle = chakra("div", {
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "52px",
    height: "52px",
    borderRadius: "full",
    borderWidth: "1px",
    borderStyle: "solid",
  },
  variants: {
    tone: {
      onPanel: {
        background: "rgba(255, 255, 255, 0.18)",
        borderColor: "rgba(255, 255, 255, 0.36)",
        color: "white",
      },
      lilac: {
        background: "vbaby.lilac",
        borderColor: "transparent",
        color: "vbaby.purple",
      },
      success: {
        background: "rgba(205, 249, 232, 0.16)",
        borderColor: "rgba(205, 249, 232, 0.35)",
        color: "vbaby.mint",
      },
    },
  },
  defaultVariants: { tone: "onPanel" },
});

export const PanelTitle = chakra("h1", {
  base: {
    margin: 0,
    fontFamily: "heading",
    fontWeight: "600",
    lineHeight: "1.3",
  },
  variants: {
    size: {
      hero: { fontSize: "20px", lg: { fontSize: "24px" } },
      header: { fontSize: "18px" },
    },
  },
  defaultVariants: { size: "hero" },
});

export const PanelSubtitle = chakra("p", {
  base: {
    margin: 0,
    fontFamily: "body",
    fontSize: "14px",
    lineHeight: "1.45",
    color: "vbaby.onPanel",
  },
});

export const PanelNote = chakra("p", {
  base: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    margin: 0,
    fontFamily: "body",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "16px",
    color: "vbaby.onPanelMuted",
    "& > svg": { flexShrink: 0 },
  },
});

export const Body = chakra("div", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    padding: "20px 16px 24px",
    lg: { padding: "28px" },
  },
});

export const PriceBox = chakra("div", {
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "14px 16px",
    borderRadius: "12px",
    background: "vbaby.lilac",
  },
});

export const PriceLabel = chakra("span", {
  base: {
    fontFamily: "body",
    fontSize: "13px",
    fontWeight: "500",
    lineHeight: "18px",
    color: "vbaby.purple",
  },
});

export const PriceValue = chakra("strong", {
  base: {
    fontFamily: "numeric",
    fontSize: "24px",
    fontWeight: "700",
    lineHeight: "1.25",
    color: "vbaby.ink",
  },
});

export const PrimaryButton = chakra("button", {
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    width: "100%",
    minHeight: "50px",
    border: "none",
    borderRadius: "12px",
    background: PIX_BUTTON_BG,
    color: "white",
    fontFamily: "heading",
    fontSize: "15px",
    fontWeight: "600",
    lineHeight: "20px",
    cursor: "pointer",
    _focusVisible: {
      outline: "2px solid",
      outlineColor: "vbaby.accent",
      outlineOffset: "2px",
    },
    _disabled: { opacity: 0.4, pointerEvents: "none", cursor: "not-allowed" },
    "& > svg": { flexShrink: 0 },
  },
});

export const SecondaryButton = chakra("button", {
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    minHeight: "44px",
    padding: "0 10px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "vbaby.border",
    borderRadius: "11px",
    background: "vbaby.card",
    color: "vbaby.ink",
    fontFamily: "body",
    fontSize: "12.5px",
    fontWeight: "600",
    lineHeight: "16px",
    cursor: "pointer",
    _focusVisible: {
      outline: "2px solid",
      outlineColor: "vbaby.accent",
      outlineOffset: "2px",
    },
    "& > svg": { flexShrink: 0 },
  },
});

export const TermsLine = chakra("div", {
  base: {
    fontFamily: "body",
    fontSize: "11px",
    lineHeight: "16px",
    color: "vbaby.text3",
    textAlign: "center",
  },
});

// A altura de toque vem do padding, não de `min-height` com `align-items`: em
// `<button>` o Safari posiciona o conteúdo fora da caixa de layout, e a área de
// clique deixa de coincidir com o texto. `width: fit-content` evita que o botão
// ocupe a linha inteira e capture toques longe do rótulo.
export const BackButton = chakra("button", {
  base: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    width: "fit-content",
    padding: "12px 12px 12px 0",
    border: "none",
    background: "transparent",
    color: "vbaby.accent",
    fontFamily: "heading",
    fontSize: "14px",
    fontWeight: "600",
    lineHeight: "20px",
    textAlign: "left",
    cursor: "pointer",
    touchAction: "manipulation",
    _focusVisible: {
      outline: "2px solid",
      outlineColor: "vbaby.accent",
      outlineOffset: "2px",
    },
  },
});

export const IconNote = chakra("p", {
  base: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    margin: 0,
    fontFamily: "body",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "16px",
    color: "vbaby.text3",
    "& > svg": { flexShrink: 0 },
  },
});

// Campos próprios em vez de `Input`/`Field.Label`: componente com receita emite um
// segundo bloco na mesma classe e camada, e a receita vence as props.
export const FieldGroup = chakra("div", {
  base: { display: "flex", flexDirection: "column", gap: "6px" },
});

export const FieldLabel = chakra("label", {
  base: {
    fontFamily: "body",
    fontSize: "12.5px",
    fontWeight: "500",
    lineHeight: "18px",
    color: "vbaby.label",
  },
});

export const FieldInput = chakra("input", {
  base: {
    boxSizing: "border-box",
    width: "100%",
    height: "48px",
    padding: "12px 14px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "vbaby.border",
    borderRadius: "12px",
    background: "vbaby.card",
    color: "vbaby.ink",
    fontFamily: "body",
    fontSize: "15px",
    _placeholder: { color: "vbaby.muted" },
    _focusVisible: {
      borderColor: "vbaby.purple",
      boxShadow: FOCUS_RING,
      outline: "none",
    },
    '&[aria-invalid="true"]': { borderColor: "vbaby.danger" },
  },
});

export const FieldError = chakra("p", {
  base: {
    margin: 0,
    fontFamily: "body",
    fontSize: "12px",
    lineHeight: "16px",
    color: "vbaby.danger",
  },
});
